/**
 * Marca de "pedido propio" para los buscadores v1 (BuscadorConsultaMedica y
 * BuscadorEstudio), que procesan las respuestas de los reducers compartidos
 * estudioTurnos / consultaMedTurnos con un useEffect en vez de un callback.
 *
 * Ionic mantiene montadas las páginas anteriores al navegar hacia adelante, así
 * que puede haber varias instancias escuchando el mismo reducer. Sin esta marca,
 * la instancia que no había buscado nada también procesaba la respuesta y pisaba
 * turnosDisponibles con su estado vacío (GruposMedico[].Id = "", dtoBusqueda =
 * false); la toma del turno salía sin contexto y el backend la rechazaba con 400
 * ("$.GruposMedico[0].Id could not be converted to System.Int64").
 *
 * Uso: el componente guarda un contador en un useRef, despacha sus pedidos con
 * crearDispatchPropio (cuenta cada LOADING) y al principio del efecto consulta
 * consumirRespuestaPropia (efecto principal) o respuestaEsPropia (efectos
 * secundarios que corren antes y no consumen).
 *
 * Límite conocido: el reducer no identifica pedidos, así que si dos instancias
 * disparan pedidos al mismo tiempo, la respuesta que llega primero la toma la que
 * tenga contador. En la práctica el paciente opera una sola página por vez.
 */

const hayRespuesta = (slice) => {
  const { data, error } = slice;
  const hayData =
    data !== undefined && data !== null && data !== false && data !== "";
  const detalle = error ? error.detail : undefined;
  const hayError = detalle !== undefined && detalle !== null && detalle !== "";
  return hayData || hayError;
};

/**
 * Envuelve el dispatch del reducer: cada LOADING que pasa por acá cuenta como un
 * pedido propio en vuelo. El resto de las acciones pasan sin tocar.
 */
export const crearDispatchPropio =
  (dispatch, contadorRef, tipoLoading) => (action) => {
    if (action && action.type === tipoLoading) {
      contadorRef.current += 1;
    }
    dispatch(action);
  };

/**
 * Para efectos que no consumen la respuesta (corren antes del principal).
 * Devuelve true si no hay respuesta que evaluar, o si la hay y es de esta
 * instancia.
 */
export const respuestaEsPropia = (slice, contadorRef) => {
  if (!hayRespuesta(slice)) return true;
  return !slice.loading && contadorRef.current > 0;
};

/**
 * Para el efecto principal. Devuelve true si el efecto debe seguir: no hay
 * respuesta que evaluar, o la respuesta es de esta instancia (y la descuenta).
 * Devuelve false ante una respuesta ajena, o ante el dato viejo que queda en el
 * reducer mientras corre un pedido nuevo (loading).
 */
export const consumirRespuestaPropia = (slice, contadorRef) => {
  if (!hayRespuesta(slice)) return true;
  if (slice.loading || contadorRef.current <= 0) return false;
  contadorRef.current -= 1;
  return true;
};
